<template>
  <div class="orders">
    <PageHeader :title="pageTitle" />

    <div class="orders__filters">
      <div class="orders__filters__item">
        <label for="FilterSelect">{{ getMessageByLang('SelectStatus', 'Выберите статус') }}</label>
        <select id="FilterSelect" class="app-select" v-model="filter.status" @change="onChangeFilter">
          <option :value="null">{{ getMessageByLang('AllText', 'Все') }}</option>
          <option v-for="status in OrdersStatuses" :key="status" :value="status">{{ getOrderStatus(status) }}</option>
        </select>
      </div>

      <div class="orders__filters__item">
        <label for="FilterSelect">{{ getMessageByLang('clientText', 'Клиент') }}</label>
        <AutoComplete :data-items="clients" :text-field="'name'" :data-item-key="'id'" @change="handleChangeUser"
          @filterchange="handleFilterChangeClients" />
      </div>

      <div class="orders__filters__item">
        <label for="FilterSelect">Дата</label>
        <div v-if="isShowDatesBlock" class="app-datepicker-block">
          <DatePicker name="dateStart" culture="ru-RU" @change="onChangeDate">
          </DatePicker>
          <span> - </span>
          <DatePicker name="dateEnd" culture="ru-RU" @change="onChangeDate">
          </DatePicker>
        </div>
      </div>
    </div>

    <div class="orders__filters-control">
      <Button :theme-color="'primary'" @click="clearFilter">{{ getMessageByLang('clearTExt', 'Очистить') }}
      </Button>
    </div>

    <VLoader v-if="isLoading" />
    <div v-else>
      <VEmpty v-if="!orders[0]" />
      <div v-else>
        <Grid ref="grid" :data-items="orders" :columns="columns">
          <template v-slot:client="{ props }">
            <td class="k-command-cell k-table-td">
              {{ props.dataItem.name ? props.dataItem.name : props.dataItem?.client?.name || "-" }}
            </td>
          </template>
          <template v-slot:order_created_at="{ props }">
            <td class="k-command-cell k-table-td">
              {{ getFormatedDate(props?.dataItem?.created_at || null) }}
            </td>
          </template>
          <template v-slot:order_update_at="{ props }">
            <td class="k-command-cell k-table-td">
              {{ getFormatedDate(props?.dataItem?.updated_at || null) }}
            </td>
          </template>
          <template v-slot:payments="{ props }">
            <td class="k-command-cell k-table-td">
              <ul class="orders__list-payments">
                <li v-for="payment in props.dataItem.payments" :key="payment">
                  {{ payment.amount }}
                </li>
              </ul>
            </td>
          </template>
          <template v-slot:status="{ props }">
            <td class="k-command-cell k-table-td">
              {{ getOrderStatus(props?.dataItem?.status || '') }}
            </td>
          </template>

          <template v-slot:communication="{ props }">
            <div class="k-command-cell k-table-td">
              {{ communicationMethod(props.dataItem, 'type') }}
            </div>
          </template>

          <template v-slot:contacts="{ props }">
            <div class="k-command-cell k-table-td">
              {{ communicationValue(props.dataItem) }}
            </div>
          </template>

          <template v-slot:control="{ props }">
            <td class="k-command-cell k-table-td">
              <div class="cell-control" v-if="getRole() === 'admin'">
                <Button :title="getMessageByLang('editText', 'Редактировать')" :theme-color="'primary'"
                  :svg-icon="pencilIcon" @click="onActionOrder(props.dataItem.id, 'edit')">
                </Button>
                <Button :title="getMessageByLang('cancelText', 'Отмена')" :svg-icon="cancelIcon"
                  @click="onActionOrder(props.dataItem.id, 'cancel')">
                </Button>
                <Button :title="getMessageByLang('deteleteText', 'Удалить')" :svg-icon="trashIcon"
                  @click="onActionOrder(props.dataItem.id, 'delete')">
                </Button>
              </div>
            </td>
          </template>
        </Grid>
      </div>
    </div>

    <ModalDelete v-if="isShowModalConfirmDelete" @accept="modalDeleteAccept" @cancel="modalDeleteCancel"
      text="Вы уверены, что заказ нужно удалить?" />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

import { getMessageByLang } from "@/utils/translate.js";
import { getRespErrorMessage } from "@/utils/errors.js";
import { Grid } from "@progress/kendo-vue-grid";
import { Button } from "@progress/kendo-vue-buttons";
import { useRouter } from "vue-router";
import { getFormatedDate } from "@/utils/date.js";
import { cancelIcon, pencilIcon, trashIcon } from "@progress/kendo-svg-icons";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import { AutoComplete } from "@progress/kendo-vue-dropdowns";
import ModalDelete from '@/components/modals/ModalDelete.vue';
import moment from 'moment';
import { getRole } from "@/utils/router";

import VLoader from "@/ui-kit/components/VLoader.vue";
import VEmpty from "@/ui-kit/components/VEmpty.vue";
import PageHeader from "@/components/page/PageHeader.vue";

import api from "@/api";
import { OrdersStatuses } from "@/constants/Orders";
import { getOrderStatus } from '../composables/orders';

const router = useRouter();
const isLoading = ref(true);
const orders = ref([]);
const isShowModalConfirmDelete = ref(false);
const pageTitle = getMessageByLang("ordersTxt", "Заказы");
const columns = ref([
  {
    field: 'order_number',
    title: getMessageByLang("number", "Номер")
  },
  {
    field: "client",
    title: "Клиент",
    cell: "client",
  },
  {
    field: "status",
    title: getMessageByLang("statusText", "Статус"),
    cell: 'status'
  },
  {
    field: "order_created_at",
    title: getMessageByLang("createdAtText", "Создано"),
    cell: "order_created_at",
  },
  {
    field: "order_date",
    title: getMessageByLang("updatedAtText", "Обновлено"),
  },
  {
    field: "communication",
    title: "Способ связи",
    cell: "communication",
  },
  {
    field: "contacts",
    title: "Контакты",
    cell: "contacts",
  },
  {
    field: "control",
    title: getMessageByLang("control", "Управление"),
    cell: "control",
  },
]);
const filter = ref({
  client_id: null
});
const clients = ref([]);
const isShowDatesBlock = ref(true);
const filteredUsers = ref([]);
const orderIdDelete = ref(null);

const communicationValue = (client) => {
  if(client.communication) {
    return client.communication
  }

  if(client.client.telegram) {
    return client.client.telegram;
  }

  if(client.client.telegram) {
    return client.client.whatsapp;
  }

  return '-'
}

const communicationMethod = (client) => {
  if (client.method_of_communication === 'telegram') {
    return 'Telegram'
  }

  if (client.method_of_communication === "phone") {
    return 'Телефон'
  }

  if (client.method_of_communication === "whatsapp") {
    return 'Whatsapp'
  }

  return 'Telegram'

}

const modalDeleteAccept = async () => {
  try {
    await api.order.delete(orderIdDelete.value);
    getOrders();

  } catch (err) {
    console.error(err);
    alert('Ошибка удаления. Попробуйте снова')

  } finally {
    isShowModalConfirmDelete.value = false;
  }
}

const modalDeleteCancel = () => {
  isShowModalConfirmDelete.value = false;
}

const updateDatesBlock = () => {
  isShowDatesBlock.value = false;

  setTimeout(() => {
    isShowDatesBlock.value = true;
  }, 0);
}

const handleFilterChangeClients = (event) => {
  try {
    this.foo = event.filter.value;

    if (event && event.filter && event.filter.value) {
      const filter = event.filter.value.toLowerCase();

      filteredUsers.value = clients.value.filter(user =>
        user.name.toLowerCase().includes(filter)
      );
    }

  } catch (err) {
    console.error(err);
  }

}

const handleChangeUser = (event) => {
  try {
    const clientName = event.target.value;
    filter.value.client_id = null;

    if (!clientName) {
      getOrders();
      return
    }

    const findClient = clients.value.filter((client) => {
      if (client.name === clientName) {
        return client
      }
    })

    if (findClient[0]) {
      filter.value.client_id = findClient[0].id;
      console.log('filter.value.client', filter.value.client)
      onChangeFilter();
    }

  } catch (err) {
    console.error(err);
  }
}

const onChangeDate = (date) => {
  if (date.target.name === 'dateStart') {
    if (date.value !== null) {
      filter.value.start = moment(date.value).format('YYYY-MM-DD');
    } else {
      filter.value.start = null;
    }

  } else {
    if (date.value !== null) {
      filter.value.end = moment(date.value).format('YYYY-MM-DD');
    } else {
      filter.value.end = null;
    }
  }

  onChangeFilter();
}

const clearFilter = () => {
  updateDatesBlock();
  filter.value = {
    client_id: null
  };
  onChangeFilter();
}

const onChangeFilter = () => {
  getOrders();
}

const getOrders = async () => {
  try {
    isLoading.value = true;

    const filters = {
      client_id: filter.value.client_id || null,
      status: filter.value.status || null,
      date_from: filter.value.start,
      date_to: filter.value.end,
      limit: 100
    }

    const response = await api.orders.getList(filters);
    orders.value = response.data.data;

  } catch (err) {
    console.error(err);
    orders.value = [];

    const message = getRespErrorMessage(err);
    alert(message);

  } finally {
    isLoading.value = false;
  }
};

const getClients = async () => {
  try {
    const responseClients = await api.clients.getList();
    clients.value = responseClients.data.data.slice();

  } catch (err) {
    console.error(err);
    clients.value = [];
  }
}


const onActionOrder = async (orderId, action) => {
  try {
    switch (action) {
      case 'edit':
        router.push(`/admin/orders/${orderId}`);
        break;

      case 'cancel':
        await api.order.cancel(orderId);
        getOrders();
        break;

      case 'delete':
        isShowModalConfirmDelete.value = true;
        orderIdDelete.value = orderId;
        break;
    }

  } catch (err) {
    console.error(err);

    let message = '';

    if (action === 'delete') {
      message = 'Ошибка удаления. Попробуйте снова'
    } else {
      message = getRespErrorMessage(err);
    }

    alert(message);
  }
}

onMounted(() => {
  //kendo.culture('ru-RU');

  getOrders();
  getClients();
});
</script>

<style lang="scss" scoped>
.orders {
  &__list-payments {
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
  }

  &__filters {
    margin-bottom: 1rem;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;

    @media all and (max-width: 1300px) {
      display: flex;
      flex-direction: column;
    }

    &__item {
      & label {
        font-size: 12px;
        color: #999;
      }
    }
  }
}

.orders__filters-control {
  margin-bottom: 1rem;
  display: flex;
  justify-content: end;
}
</style>